import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import './ChooseYourPathModal.scss';

interface ChooseYourPathModalProps {
  // Add props if needed
}

const ChooseYourPathModal = (props: ChooseYourPathModalProps) => {
  const { characters, createNewCharacter, fetchCharacters, isLoading } = useUser();
  // Start with modal closed
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHero, setSelectedHero] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [characterName, setCharacterName] = useState('');

  // Check if user has no characters and show modal
  useEffect(() => {
    console.log('Checking characters array:', characters);
    
    // Only show modal when characters are loaded and the array is empty
    if (!isLoading && Array.isArray(characters)) {
      if (characters.length === 0) {
        console.log('No characters found, showing modal');
        setIsOpen(true);
      } else {
        console.log('Characters found, hiding modal');
        setIsOpen(false);
      }
    }
  }, [characters, isLoading]);

  const closeModal = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // Only allow closing if character creation is complete
    if (Array.isArray(characters) && characters.length > 0) {
      setIsOpen(false);
    } else {
      console.log('Cannot close modal - no characters created yet');
    }
  };

  const selectHero = (hero: string) => {
    setSelectedHero(hero);
  };

  const confirmSelection = async () => {
    if (selectedHero && characterName.trim()) {
      setIsCreating(true);
      setError(null);
      try {
        // Map hero type to the API expected format
        const heroType = selectedHero === 'phantom' ? 'Phantom' : 'Dark Cleric';
        
        // Create the new character
        await createNewCharacter({
          name: characterName.trim(),
          type: heroType
        });
        
        // Refresh the character list
        await fetchCharacters();
        
        // Close the modal
        setIsOpen(false);
      } catch (err) {
        console.error('Error creating character:', err);
        setError(err instanceof Error ? err.message : 'Failed to create character');
      } finally {
        setIsCreating(false);
      }
    } else if (!characterName.trim()) {
      setError('Please enter a character name');
    }
  };

  return (
    <div className="m-choose-your-path" style={{ display: isOpen ? 'flex' : 'none' }}>
      <div className="m-choose-your-path__container">
        <a href="" className="m-choose-your-path__close" onClick={closeModal}><img src="assets/images/close.svg" alt="" /></a>
        <div className="m-choose-your-path__light"></div>
        <div className="m-choose-your-path__header">
          <h1>Choose Your <span>Path</span></h1>
          <img src="assets/images/chose-your-parh-title-icon@1x.png" srcSet="assets/images/chose-your-parh-title-icon@2x.png 2x" alt="" />
        </div>
        <p className="m-choose-your-path__intro">Select your pre-character to begin your adventure in Phantom Clash.</p>
        
        <div className="m-choose-your-path__name-input">
          <label htmlFor="character-name">Character Name</label>
          <input
            type="text"
            id="character-name"
            value={characterName}
            onChange={(e) => setCharacterName(e.target.value)}
            placeholder="Enter character name"
          />
        </div>
        
        <div className="m-choose-your-path__heroes">
          <div className={`m-choose-your-path__heroes-item phantom ${selectedHero === 'phantom' ? 'active' : ''}`} onClick={() => selectHero('phantom')}>
            <div className="m-choose-your-path__heroes-item-text">
              <h1>Phantom</h1>
              <button>Choose</button>
            </div>
            <img className="m-choose-your-path__heroes-item-img1" src="assets/images/heroes/phantom-hero@1x.png" srcSet="assets/images/heroes/phantom-hero@2x.png 2x" alt="" />
            <img className="m-choose-your-path__heroes-item-img2" src="assets/images/heroes/phantom-hero-hover@1x.png" srcSet="assets/images/heroes/phantom-hero-hover@2x.png 2x" alt="" />
            <div className="m-choose-your-path__heroes-item-video"><video src="assets/images/heroes/phantom.mp4" autoPlay loop muted playsInline></video></div>
          </div>
          <div className={`m-choose-your-path__heroes-item darkcleric ${selectedHero === 'darkcleric' ? 'active' : ''}`} onClick={() => selectHero('darkcleric')}>
            <div className="m-choose-your-path__heroes-item-text">
              <h1>Dark Cleric</h1>
              <button>Choose</button>
            </div>
            <img className="m-choose-your-path__heroes-item-img1" src="assets/images/heroes/darkcleric-hero@1x.png" srcSet="assets/images/heroes/darkcleric-hero@2x.png 2x" alt="" />
            <img className="m-choose-your-path__heroes-item-img2" src="assets/images/heroes/darkcleric-hero-hover@1x.png" srcSet="assets/images/heroes/darkcleric-hero-hover@2x.png 2x" alt="" />
            <div className="m-choose-your-path__heroes-item-video"><video src="assets/images/heroes/darkcleric.mp4" autoPlay loop muted playsInline></video></div>
          </div>
        </div>
        
        {error && (
          <div className="m-choose-your-path__error">
            {error}
          </div>
        )}
        
        <div className="m-choose-your-path__separator"><img src="assets/images/separator-3.svg" alt="" /></div>
        <p className="m-choose-your-path__text">This is a pre-selection to set up your initial experience. Once in-game, you can choose your hero, and this selection will update automatically</p>
        <button 
          className="m-choose-your-path__button" 
          disabled={!selectedHero || !characterName.trim() || isCreating}
          onClick={confirmSelection}
        >
          {isCreating ? 'Creating...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

export default ChooseYourPathModal; 
